import axios, { AxiosError } from 'axios';
import { getToken, loginRedirectOnce } from '../services/auth.service';
import { BASE_URL, RECAPTCHA_CLIENT_SECRET } from 'constants/db.constants';
import { msalInstance } from '../index';
import { store } from '../state/store';
import { trimAllStringsInFormdata, trimAllStringsInObject } from 'constants/global.constants';
import { loadReCaptchaScript } from '../utils/recaptcha';

export default class ApiService {
	static instance: ApiService;

	constructor() {
		if (ApiService.instance) {
			return ApiService.instance;
		}
		ApiService.instance = this;
	}

	static getInstance = () => ApiService.instance || new ApiService();

	static getServerUrl = () => BASE_URL /*SERVER_URLS[globalEndpoints.NODE_ENV as Environment]*/;

	getRecaptchaToken = async (partialUrl: string): Promise<string> => {
		if (typeof window === 'undefined') {
			return '';
		}

		await loadReCaptchaScript();

		const grecaptcha = (window as any).grecaptcha;
		if (!grecaptcha?.execute) {
			return '';
		}

		const onlyLettersRegex = /[^a-zA-Z]/g;
		const name = partialUrl.replace(onlyLettersRegex, '_');
		const recaptchaToken = await grecaptcha.execute(RECAPTCHA_CLIENT_SECRET, { action: name });
		return recaptchaToken;
	};

	get = async (
		partialUrl: string,
		params: any = {},
		headersAdditionalParameters: any = {},
		requestAdditionalParameters: any = {},
		toGlobalRouter: boolean = false
	): Promise<any> => {
		const userPermission = store.getState().user.userPermission;
		const companyId = store.getState().user.details?.companyId;
		const jwt = await getToken(msalInstance);

		if (!jwt) {
			await loginRedirectOnce(msalInstance);
			return Promise.reject();
		}

		const headers = {
			Authorization: `Bearer ${jwt}`,
			'x-od-company-id': companyId,
			//'x-one-digital-recaptcha-token': await this.getRecaptchaToken(partialUrl),
			...headersAdditionalParameters,
		};

		try {
			if (Object.keys(params).length) {
				Object.keys(params).forEach((key) => {
					partialUrl = partialUrl.replace(`:${key}`, params[key]);
				});
			}

			const completeUrl = toGlobalRouter
				? ApiService.getServerUrl() + partialUrl
				: ApiService.getServerUrl() + '/' + userPermission + partialUrl;

			const res = await axios.get(completeUrl, {
				timeout: 60000,
				headers,
				...requestAdditionalParameters,
			});
			return await Promise.resolve(res);
		} catch (error: any) {
			return this.handleResponseError(error);
		}
	};

	post = async (
		partialUrl: string,
		data: Record<string, any> = {},
		requestAdditionalParameters: any = {},
		headersAdditionalParameters: any = {}
	): Promise<any> => {
		let copyData: any = data;
		try {
			if (typeof copyData === 'string') {
				copyData = copyData.trim();
			} else if (copyData instanceof FormData) {
				copyData = trimAllStringsInFormdata(copyData);
			} else {
				trimAllStringsInObject(copyData);
			}
		} catch (e) {
			console.log('trimAllStrings::' + e);
			copyData = data;
		}
		const userPermission = store.getState().user.userPermission;
		const [jwt, recaptchaToken] = await Promise.all([getToken(msalInstance), this.getRecaptchaToken(partialUrl)]);

		if (!jwt) {
			await loginRedirectOnce(msalInstance);
			return Promise.reject();
		}

		const companyId = store.getState().user.details?.companyId;
		const headers = {
			'x-od-company-id': companyId,
			'x-one-digital-recaptcha-token': recaptchaToken,
			Authorization: `Bearer ${jwt}`,
			...headersAdditionalParameters,
		};

		const completeUrl = ApiService.getServerUrl() + '/' + userPermission + partialUrl;

		try {
			const res = await axios.post(completeUrl, copyData, {
				timeout: 60000,
				headers,
				...requestAdditionalParameters,
			});
			return await Promise.resolve(res);
		} catch (error: any) {
			return this.handleResponseError(error);
		}
	};

	handleResponseError = (error: AxiosError<any>) => {
		const apiError = {
			message: error.message,
			data: error?.response?.data,
			statusCode: error?.response?.status,
		};
		return Promise.reject(apiError);
	};
}
